import { createApi } from '@reduxjs/toolkit/dist/query/react';
import axiosBaseQuery from '../../../axios/axiosBaseQuery';
import { Base64 } from '../../../util/Base64';
import type {
  ReportRealTimeResult,
  ReportRealTimeSpec,
} from './ReportRealTime.model';
import {
  Report,
  ReportQuery,
  ReportQueryParams,
  ReportRequest,
  ReportType,
} from './Report.model';
import { reportsMock, reportTypeMocks } from './reports.mock';
import Util from '../../../util/Util';
import { serializeReportSpec } from './Report.util';

const ReportBaseURL = new Util().getReportURL();
export const ReportAPI = createApi({
  reducerPath: 'report',
  tagTypes: ['report', 'reportSchedule', 'reportType'],
  baseQuery: axiosBaseQuery({ baseUrl: ReportBaseURL }),
  endpoints: (build) => ({
    // Reports
    getReports: build.query<ReportQuery, ReportQueryParams>({
      query: (params) => ({
        url: `/main/workspace/${Base64.encode(
          params.workspaceId ?? ''
        )}/reports`,
        method: 'GET',
        params,
      }),
      providesTags: ['report'],
      // queryFn: (params) => ({ data: reportsMock as Report[] }),
    }),
    getReport: build.query<Report, { reportId: string; workspaceId: string }>({
      query: ({ reportId, workspaceId }) => ({
        url: `/main/workspace/${Base64.encode(
          workspaceId
        )}/reports/${reportId}`,
        method: 'GET',
      }),
      providesTags: ['report'],
    }),
    createReport: build.mutation<
      any,
      { spec: ReportRequest; workspaceId: string | undefined }
    >({
      query: ({ spec, workspaceId }) => ({
        url: `/main/workspace/${Base64.encode(
          workspaceId || ''
        )}/reports?async=true`,
        method: 'POST',
        data: serializeReportSpec(spec),
      }),
      invalidatesTags: ['report'],
    }),
    deleteReport: build.mutation<
      any,
      { reportId: string; workspaceId: string }
    >({
      query: ({ reportId, workspaceId }) => ({
        url: `/main/workspace/${Base64.encode(
          workspaceId
        )}/reports/${reportId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['report'],
    }),
    // Report Schedules
    getReportSchedules: build.query<
      any,
      { workspaceId: string; groupId?: string }
    >({
      query: ({ workspaceId, groupId }) => ({
        url: `/main/workspace/${Base64.encode(workspaceId)}/report-schedules`,
        method: 'GET',
        params: { groupId },
      }),
      providesTags: ['reportSchedule'],
    }),
    getReportSchedule: build.query<
      any,
      { encodedWorkspaceID: string; scheduleID: string }
    >({
      query: ({ encodedWorkspaceID, scheduleID }) => ({
        url: `/main/workspace/${encodedWorkspaceID}/report-schedules/${scheduleID}`,
        method: 'GET',
      }),
      providesTags: ['reportSchedule'],
    }),
    createReportSchedule: build.mutation<
      any,
      { encodedWorkspaceID: string; schedule: any }
    >({
      query: ({ encodedWorkspaceID, schedule }) => ({
        url: `/main/workspace/${encodedWorkspaceID}/report-schedules`,
        method: 'POST',
        data: schedule,
      }),
      invalidatesTags: ['reportSchedule'],
    }),
    updateReportSchedule: build.mutation<
      any,
      { encodedWorkspaceID: string; scheduleID: string; schedule: any }
    >({
      query: ({ encodedWorkspaceID, scheduleID, schedule }) => ({
        url: `/main/workspace/${encodedWorkspaceID}/report-schedules/${scheduleID}`,
        method: 'PUT',
        data: schedule,
      }),
      invalidatesTags: ['reportSchedule'],
    }),
    deleteReportSchedule: build.mutation<
      any,
      { encodedWorkspaceID: string; scheduleID: string; schedule?: any }
    >({
      query: ({ encodedWorkspaceID, scheduleID, schedule }) => ({
        url: `/main/workspace/${encodedWorkspaceID}/report-schedules/${scheduleID}`,
        method: 'DELETE',
        data: schedule,
      }),
      invalidatesTags: ['reportSchedule'],
    }),
    // Report Types
    getReportTypes: build.query<ReportType[], string>({
      query: (workspaceId) => ({
        url: `/main/workspace/${Base64.encode(workspaceId)}/report-types`,
        method: 'GET',
      }),
      //mocking
      // queryFn: () => ({ data: reportTypeMocks }),
      providesTags: ['reportType'],
    }),
    getReportType: build.query<
      any,
      { encodedWorkspaceID: string; reportTypeID: string }
    >({
      query: ({ encodedWorkspaceID, reportTypeID }) => ({
        url: `/main/workspace/${encodedWorkspaceID}/report-types/${reportTypeID}`,
        method: 'GET',
      }),
      providesTags: ['reportType'],
    }),
    createReportType: build.mutation<
      any,
      { encodedWorkspaceID: string; schedule: any }
    >({
      query: ({ encodedWorkspaceID, schedule }) => ({
        url: `/main/workspace/${encodedWorkspaceID}/report-types`,
        method: 'POST',
        data: schedule,
      }),
      invalidatesTags: ['reportType'],
    }),
    updateReportType: build.mutation<
      any,
      { encodedWorkspaceID: string; reportTypeID: string; schedule: any }
    >({
      query: ({ encodedWorkspaceID, reportTypeID, schedule }) => ({
        url: `/main/workspace/${encodedWorkspaceID}/report-types/${reportTypeID}`,
        method: 'PUT',
        data: schedule,
      }),
      invalidatesTags: ['reportType'],
    }),
    deleteReportType: build.mutation<
      any,
      { encodedWorkspaceID: string; reportTypeID: string; schedule?: any }
    >({
      query: ({ encodedWorkspaceID, reportTypeID, schedule }) => ({
        url: `/main/workspace/${encodedWorkspaceID}/report-types/${reportTypeID}`,
        method: 'DELETE',
        data: schedule,
      }),
      invalidatesTags: ['reportType'],
    }),
    // Test endpoints
    createReportWithoutPersisting: build.mutation<any, ReportRealTimeSpec>({
      query: (spec) => ({
        url: `/main/reports/test`,
        method: 'POST',
        data: spec,
      }),
    }),
    createReportWithoutPersistingPDF: build.mutation<Blob, ReportRealTimeSpec>({
      query: (spec) => ({
        url: `/main/reports/test/pdf`,
        method: 'POST',
        data: spec,
        responseType: 'blob',
        headers: { Accept: 'application/pdf' },
      }),
    }),
  }),
});

export default ReportAPI;
